import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  Tooltip,
  Select,
  useColorModeValue,
  useDisclosure,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonSearch from 'components/commonSearchBar/CommonSearch';
import CommonTable from 'components/commonTable/CommonTable';
import DeleteModal from 'components/popUpModel/DeleteModal';
import MerchantFilterDropdown from 'components/MerchantFilterDropdown';
import GroupManagementModal from 'components/GroupManagementModal';
import { useDispatch, useSelector } from 'react-redux';
import {
  DeleteStationData,
  ListStationData,
  SyncStationSheet,
} from 'reactRedux/thunks/stationThunk/stationThunk';
import {
  stationStatus,
  stationStatusColors,
  networkStatus,
  posStatus,
  posStatusColors,
  merchantStatusMap,
  merchantStatusColors,
  sideBarRoutes,
} from 'utils/StaticData';
import useRegions from 'hooks/useRegions';
import { Link, useLocation } from 'react-router-dom';
import { PiPencil } from 'react-icons/pi';
import StationSummaryCard from './StationSummaryCard';
import useListState from 'hooks/useListState';
import useMerchantExclusionFilter from 'hooks/useMerchantExclusionFilter';
import useMerchantGroups from 'hooks/useMerchantGroups';

const StationList = () => {
  const dispatch = useDispatch();
  const stationDeleteRef = useRef(null);
  const location = useLocation();
  const { regions } = useRegions();
  const tableRef = useRef(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const { isOpen, onClose } = useDisclosure();
  const textColor = useColorModeValue('secondaryGray.900', 'white');

  // State for excluded merchants
  const [excludeMerchant, setExcludeMerchant] = useState([]);

  // Use merchant exclusion filter hook
  const {
    searchTerm,
    setSearchTerm,
    isMerchantPopoverOpen,
    openMerchantPopover,
    closeMerchantPopover,
    filteredMerchants,
    handleApplySelection,
    handleCancelSelection,
    handleSelectAll,
    handleMerchantToggle,
    isSelectAllChecked,
    isMerchantChecked,
    getSelectedMerchantText,
    handleClearSelection,
    // Unassigned
    excludeUnassigned,
    pendingExcludeUnassigned,
    handleUnassignedToggle,
    setExcludeUnassigned,
    setPendingExcludeUnassigned,
    isLoadingMerchants,
    // Merchant state
    pendingExcludeMerchant,
    setPendingExcludeMerchant,
  } = useMerchantExclusionFilter(excludeMerchant, setExcludeMerchant);

  // Use the reusable hook for URL-synced filter state
  const {
    params,
    setParam,
    setParams,
    debouncedParams,
    page,
    setPage,
    resetAll,
    currentUrl,
  } = useListState({
    defaults: {
      search: '',
      limit: 10,
      sortBy: location?.state?.status === 'offline' ? -1 : 1,
      sortKey: location?.state?.status === 'offline' ? 'status' : '',
      network: '',
      status: '',
      posStatus: '',
      condition: '',
      merchantStatus: '',
      stockOccupancy: '',
      availablePBsOccupancy: '',
      offlineMinutesFilter: '',
      excludeMerchant: '',
      excludeUnassigned: '',
      region: '',
    },
    debounceKeys: ['search'],
    debounceMs: 500,
  });

  // Initialize excludeMerchant and excludeUnassigned from URL params
  useEffect(() => {
    const excludeMerchantParam = params.excludeMerchant;
    if (excludeMerchantParam) {
      const merchantIds = excludeMerchantParam.split(',').filter(Boolean);
      if (merchantIds.length > 0) {
        setExcludeMerchant(
          merchantIds.map((id) => ({ merchantId: Number(id) })),
        );
      }
    }

    const excludeUnassignedParam = params.excludeUnassigned;
    if (excludeUnassignedParam === '1') {
      setExcludeUnassigned(true);
      setPendingExcludeUnassigned(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Sync excludeMerchant to URL when it changes
  useEffect(() => {
    // Only sync if excludeMerchant has items with merchantId
    if (excludeMerchant && excludeMerchant.length > 0) {
      const validMerchants = excludeMerchant.filter((m) => m && m.merchantId);
      if (validMerchants.length > 0) {
        const merchantIds = validMerchants.map((m) => m.merchantId).join(',');
        setParams({ excludeMerchant: merchantIds }, { resetPage: false });
      }
    } else {
      setParams({ excludeMerchant: '' }, { resetPage: false });
    }
  }, [excludeMerchant, setParams]);

  useEffect(() => {
    setParams(
      { excludeUnassigned: excludeUnassigned ? '1' : '' },
      { resetPage: false },
    );
  }, [excludeUnassigned, setParams]);

  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isLoading, stationListData, isStationSheetLoading } = useSelector(
    (state) => state.StationSlice,
  );

  // Merchant groups hook for group management in exclude filter
  const {
    merchantGroups,
    selectedGroup,
    isCreatingGroup,
    isUpdatingGroup,
    isDeleting,
    groupModalData,
    setGroupModalData,
    isGroupModalOpen,
    closeGroupModal,
    handleGroupSelection,
    openCreateGroupModal,
    openEditGroupModal,
    handleGroupModalSubmit,
    handleGroupModalCancel,
    handleGroupModalDelete,
    handleGroupMerchantSelection,
    fetchGroups,
  } = useMerchantGroups();

  // Fetch station-specific groups on mount
  useEffect(() => {
    fetchGroups('station');
  }, [fetchGroups]);

  // Get merchant list for group management modal
  const { merchantListData: merchantNameList } = useSelector(
    (state) => state.MerchantSlice,
  );

  const getStationList = useCallback(() => {
    const apiParams = {
      page: page,
      limit: params.limit || 10,
      search: debouncedParams.search,
      sortKey: params.sortKey,
      sortBy: params.sortBy,
    };

    // Add filters only if they have values
    if (params.network) apiParams.network = params.network;
    if (params.status) apiParams.status = params.status;
    if (params.posStatus) apiParams.posStatus = params.posStatus;
    if (params.condition) apiParams.condition = params.condition;
    if (params.merchantStatus) apiParams.merchantStatus = params.merchantStatus;
    if (params.stockOccupancy) apiParams.stockOccupancy = params.stockOccupancy;
    if (params.availablePBsOccupancy) {
      apiParams.availablePBsOccupancy = params.availablePBsOccupancy;
    }
    if (params.offlineMinutesFilter) {
      apiParams.offlineMinutesFilter = params.offlineMinutesFilter;
    }
    if (params.region) apiParams.region = params.region;

    let excludeMerchantIds = [];

    if (params.excludeMerchant) {
      excludeMerchantIds = params.excludeMerchant.split(',').filter(Boolean);
    }

    if (excludeMerchantIds.length > 0) {
      apiParams.excludeMerchant = excludeMerchantIds;
    }

    const isExcludeUnassigned = params.excludeUnassigned === '1';
    if (isExcludeUnassigned) {
      apiParams.excludeUnassigned = true;
    }

    dispatch(ListStationData(apiParams));
  }, [
    dispatch,
    page,
    params.limit,
    debouncedParams.search,
    params.sortKey,
    params.sortBy,
    params.network,
    params.status,
    params.posStatus,
    params.condition,
    params.merchantStatus,
    params.stockOccupancy,
    params.availablePBsOccupancy,
    params.offlineMinutesFilter,
    params.excludeMerchant,
    params.excludeUnassigned,
    params.region,
  ]);

  useEffect(() => {
    getStationList();
  }, [getStationList]);

  // Effect to handle scrolling and filtering based on location state
  useEffect(() => {
    if (location?.state?.status === 'offline') {
      setParam('sortBy', -1);
      setParam('sortKey', 'status');

      // Only scroll on initial load with offline status
      if (initialLoad) {
        const timer = setTimeout(() => {
          if (tableRef.current) {
            tableRef.current.scrollIntoView({ behavior: 'smooth' });
          }
        }, 300);
        setInitialLoad(false);
        return () => clearTimeout(timer);
      }
    } else {
      setInitialLoad(true);
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state?.status]);

  // Helper function to format date as dd/mm/yyyy hh:mm:ss
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // Build returnUrl state for Link navigation
  const returnState = { returnUrl: `/admin/stations${currentUrl}` };

  const columnHelper = createColumnHelper();
  const columns = [
    // 1. Station ID
    columnHelper.accessor((row) => row?.stationId, {
      id: 'stationId',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Station ID
        </Text>
      ),
      cell: (info) => (
        <Link
          to={`/admin/stations/details/${info.getValue()}`}
          state={returnState}
          className="btn btn-primary rounded-2 p-1"
        >
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {info.getValue()}
          </Text>
        </Link>
      ),
    }),
    // 2. Merchant
    columnHelper.accessor((row) => row?.merchantName, {
      id: 'merchantName',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Merchant
        </Text>
      ),
      cell: (info) => {
        const merchantName = info.getValue();
        const merchantId = info.row.original?.merchantId;
        const stationId = info.row.original?.stationId;
        const needsTooltip = merchantName && merchantName.length > 25;

        if (merchantName && merchantId) {
          const content = (
            <Link
              to={
                adminDetails?.role === sideBarRoutes?.Merchant
                  ? `/admin/stations/details/${stationId}`
                  : `/admin/merchants/details/${merchantId}`
              }
              state={returnState}
              className="btn btn-primary rounded-2 p-1"
            >
              <Text
                color={textColor}
                fontSize="sm"
                fontWeight="600"
                align="left"
                whiteSpace="nowrap"
                overflow="hidden"
                textOverflow="ellipsis"
                maxW="230px"
              >
                {merchantName}
              </Text>
            </Link>
          );

          return needsTooltip ? (
            <Tooltip
              label={merchantName}
              placement="top"
              hasArrow
              bg="gray.700"
              color="white"
              fontSize="sm"
              px={3}
              py={2}
              borderRadius="md"
            >
              {content}
            </Tooltip>
          ) : (
            content
          );
        }

        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            Unassigned
          </Text>
        );
      },
    }),
    // 3. Label
    columnHelper.accessor((row) => row?.location, {
      id: 'location',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Label
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {info.getValue()}
        </Text>
      ),
    }),
    // 4. Active Order
    columnHelper.accessor((row) => row?.activeOrderCount, {
      id: 'activeOrderCount',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Active Order
        </Text>
      ),
      cell: (info) => (
        <Text
          color={textColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
        >
          {info.getValue()}
        </Text>
      ),
    }),
    // 5. PB Stock (renamed from Stock)
    columnHelper.accessor((row) => row?.pbStock, {
      id: 'pbStock',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          PB Stock
        </Text>
      ),
      cell: (info) => {
        const row = info.row.original;
        const stock = row?.pbStock ?? 0;
        const slots = row?.slots ?? 0;
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {stock} / {slots}
          </Text>
        );
      },
    }),
    // 6. Faulty Slots (new)
    columnHelper.accessor((row) => row?.faultySlots, {
      id: 'faultySlots',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Faulty Slots
        </Text>
      ),
      cell: (info) => {
        const value = info.getValue() ?? 0;
        return (
          <Text
            color={value > 0 ? 'red.500' : textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {value}
          </Text>
        );
      },
    }),
    // 7. Available PBs (new)
    columnHelper.accessor((row) => row?.availablePBs, {
      id: 'availablePBs',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Available PBs
        </Text>
      ),
      cell: (info) => {
        const row = info.row.original;
        const available = row?.availablePBs ?? 0;
        const slots = row?.slots ?? 0;
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {available} / {slots}
          </Text>
        );
      },
    }),
    // 8. Station Status (renamed from Status)
    columnHelper.accessor((row) => row?.status, {
      id: 'status',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Station Status
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        const statusText = stationStatus[status] || 'N/A';
        const statusColor = stationStatusColors[status] || 'red.500';
        return (
          <Text
            color={statusColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {statusText}
          </Text>
        );
      },
    }),
    // POS Status
    columnHelper.accessor((row) => row?.posStatus, {
      id: 'posStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          POS Status
        </Text>
      ),
      cell: (info) => {
        const pos = info.getValue();
        const posText = posStatus[pos] || 'N/A';
        const posColor = posStatusColors[pos] || 'red.500';
        return (
          <Text
            color={posColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {posText}
          </Text>
        );
      },
    }),
    // Merchant Status
    columnHelper.accessor((row) => row?.merchantStatus, {
      id: 'merchantStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Merchant Status
        </Text>
      ),
      cell: (info) => {
        const status = info.getValue();
        const statusText = merchantStatusMap[status] || 'N/A';
        const statusColor = merchantStatusColors[status] || 'gray.500';
        return (
          <Text
            color={statusColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {statusText}
          </Text>
        );
      },
    }),
    // Opening Hours - Fixed width with truncation and tooltip
    columnHelper.accessor((row) => row?.merchantHoursStatus, {
      id: 'merchantHoursStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Today Opening Hours
        </Text>
      ),
      cell: (info) => {
        const hoursText = info.getValue() || 'N/A';
        const needsTooltip = hoursText && hoursText.length > 30;

        const content = (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            maxW="260px"
          >
            {hoursText}
          </Text>
        );

        return needsTooltip ? (
          <Tooltip
            label={hoursText}
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
            px={3}
            py={2}
            borderRadius="md"
          >
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    }),
    // 10. Station Condition (moved)
    columnHelper.accessor((row) => row?.stationCondition, {
      id: 'stationCondition',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Station Condition
        </Text>
      ),
      cell: (info) => {
        const condition = info.getValue();
        if (!condition) {
          return (
            <Text
              color={textColor}
              fontSize="sm"
              fontWeight="600"
              align="left"
              whiteSpace="nowrap"
            >
              -
            </Text>
          );
        }
        if (condition === 'good') {
          return (
            <Text
              color="green.500"
              fontSize="sm"
              fontWeight="600"
              align="left"
              whiteSpace="nowrap"
            >
              Good
            </Text>
          );
        }
        const reasons = condition.split(',').map((r) => r.trim());
        return (
          <Flex direction="column" gap="2px">
            {reasons.map((reason) => (
              <Text
                key={reason}
                color={reason === 'Stock issue' ? 'orange.500' : 'red.500'}
                fontSize="sm"
                fontWeight="600"
                align="left"
                whiteSpace="nowrap"
              >
                {reason}
              </Text>
            ))}
          </Flex>
        );
      },
    }),
    // 11. Last Day's Offline Minutes (renamed + moved)
    columnHelper.accessor((row) => row?.offlineMinutes, {
      id: 'offlineMinutes',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Last Day&apos;s Offline Minutes
        </Text>
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {value ?? '-'}
          </Text>
        );
      },
    }),
    // 12. Network (moved)
    columnHelper.accessor((row) => row?.network, {
      id: 'network',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Network
        </Text>
      ),
      cell: (info) => {
        const network = info.getValue();
        const networkText = networkStatus[network] || 'N/A';
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {networkText}
          </Text>
        );
      },
    }),
    // 13. Signal (moved)
    columnHelper.accessor((row) => row?.signal, {
      id: 'signal',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Signal
        </Text>
      ),
      cell: (info) => {
        const signal = info.getValue();
        const signalText = signal !== null ? signal : 'N/A';
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {signalText}
          </Text>
        );
      },
    }),
    // 14. POS Status (moved)
    columnHelper.accessor((row) => row?.posStatus, {
      id: 'posStatus',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          POS Status
        </Text>
      ),
      cell: (info) => {
        const pos = info.getValue();
        const posText = posStatus[pos] || 'N/A';
        const posColor = posStatusColors[pos] || 'red.500';
        return (
          <Text
            color={posColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {posText}
          </Text>
        );
      },
    }),
    // 15. Last Log Time (moved)
    columnHelper.accessor((row) => row?.lastLogTime, {
      id: 'lastLogTime',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Last Log Time
        </Text>
      ),
      cell: (info) => {
        const lastLogTime = info.getValue();
        const formattedTime = formatDateTime(lastLogTime);
        return (
          <Text
            color={textColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
          >
            {formattedTime}
          </Text>
        );
      },
    }),
  ];

  const action = [
    columnHelper.accessor((row) => row?.actions, {
      id: 'actions',
      header: () => (
        <Text
          justifyContent="space-between"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          width="50px"
          whiteSpace="nowrap"
        >
          ACTION
        </Text>
      ),
      cell: (row) => (
        <Flex gap={'20px'}>
          <Link
            to={`/admin/stations/edit/${row?.row?.original?.stationId}`}
            state={returnState}
            className="btn btn-light rounded-3 p-1"
          >
            <PiPencil size={20} title="Edit" />
          </Link>
        </Flex>
      ),
    }),
  ];

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        {/* Title Row */}
        <Flex justify="flex-start" align="center" mb={4} marginTop={'50px'}>
          <Text fontSize="2xl" fontWeight="medium">
            Stations List
          </Text>
        </Flex>
        {adminDetails?.role === sideBarRoutes?.Admin && <StationSummaryCard />}
        {/* Search and Filter Dropdowns Row */}
        <Flex mb={4} gap="15px" flexWrap="wrap" align="flex-end">
          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.search ? 'green.500' : 'gray.400'}
            >
              Search
            </Text>
            <CommonSearch
              search={params.search}
              setSearch={(val) => setParam('search', val)}
              setPage={setPage}
            />
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.network ? 'green.500' : 'gray.400'}
            >
              Network
            </Text>
            <Select
              value={params.network}
              onChange={(e) => {
                setParam('network', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="1">WIFI</option>
              <option value="2">SIM</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.status ? 'green.500' : 'gray.400'}
            >
              Station Status
            </Text>
            <Select
              value={params.status}
              onChange={(e) => {
                setParam('status', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="1">Online</option>
              <option value="2">Offline</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.posStatus ? 'green.500' : 'gray.400'}
            >
              POS Status
            </Text>
            <Select
              value={params.posStatus}
              onChange={(e) => {
                setParam('posStatus', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="1">Online</option>
              <option value="2">Offline</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.merchantStatus ? 'green.500' : 'gray.400'}
            >
              Merchant Status
            </Text>
            <Select
              value={params.merchantStatus}
              onChange={(e) => {
                setParam('merchantStatus', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="close">Close</option>
              <option value="not_set">Not Set</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.condition ? 'green.500' : 'gray.400'}
            >
              Station Condition
            </Text>
            <Select
              value={params.condition}
              onChange={(e) => {
                setParam('condition', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="good">Good</option>
              <option value="critical">Bad / Critical</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.stockOccupancy ? 'green.500' : 'gray.400'}
            >
              Stock Occupancy
            </Text>
            <Select
              value={params.stockOccupancy}
              onChange={(e) => {
                setParam('stockOccupancy', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="0">0% Empty</option>
              <option value="0-25">0% - 25%</option>
              <option value="25-50">25% - 50%</option>
              <option value="50-75">50% - 75%</option>
              <option value="75-100">75% - 100%</option>
              <option value="100">100% Full</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.availablePBsOccupancy ? 'green.500' : 'gray.400'}
            >
              Available PBs Occupancy
            </Text>
            <Select
              value={params.availablePBsOccupancy}
              onChange={(e) => {
                setParam('availablePBsOccupancy', e.target.value);
              }}
              width="220px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="0">0% Empty</option>
              <option value="0-25">0% - 25%</option>
              <option value="25-50">25% - 50%</option>
              <option value="50-75">50% - 75%</option>
              <option value="75-100">75% - 100%</option>
              <option value="100">100% Full</option>
            </Select>
          </Box>

          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={params.offlineMinutesFilter ? 'green.500' : 'gray.400'}
            >
              Offline Minutes
            </Text>
            <Select
              value={params.offlineMinutesFilter}
              onChange={(e) => {
                setParam('offlineMinutesFilter', e.target.value);
              }}
              width="200px"
              bg="white"
              borderRadius="12px"
              fontWeight="500"
            >
              <option value="">All</option>
              <option value="0-10">0 - 10 min</option>
              <option value="10-30">10 - 30 mins</option>
              <option value="30-60">30 - 60 mins</option>
              <option value="60+">60+ min</option>
            </Select>
          </Box>

          {adminDetails?.role === sideBarRoutes?.Admin && (
            <Box>
              <Text
                fontSize="xs"
                fontWeight="600"
                mb="1"
                color={params.region ? 'green.500' : 'gray.400'}
              >
                Region
              </Text>
              <Select
                value={params.region}
                onChange={(e) => {
                  setParam('region', e.target.value);
                }}
                width="200px"
                bg="white"
                borderRadius="12px"
                fontWeight="500"
              >
                <option value="">All</option>
                {regions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label}
                  </option>
                ))}
              </Select>
            </Box>
          )}
          {/* Merchant Exclusion Filter */}
          <Box>
            <Text
              fontSize="xs"
              fontWeight="600"
              mb="1"
              color={
                excludeMerchant?.length > 0 || excludeUnassigned
                  ? 'green.500'
                  : 'gray.400'
              }
            >
              Exclude Merchants
            </Text>
            <MerchantFilterDropdown
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              selectedMerchantText={getSelectedMerchantText()}
              isPopoverOpen={isMerchantPopoverOpen}
              openPopover={openMerchantPopover}
              closePopover={closeMerchantPopover}
              filteredMerchants={filteredMerchants}
              onSelectAll={handleSelectAll}
              onMerchantToggle={handleMerchantToggle}
              isSelectAllChecked={isSelectAllChecked}
              isMerchantChecked={isMerchantChecked}
              onApply={() => {
                // Get the current pending values before applying
                const willBeExcluded = pendingExcludeUnassigned;
                const currentPendingMerchants = pendingExcludeMerchant;

                handleApplySelection();

                const merchantIds = currentPendingMerchants
                  ?.filter((m) => m && m.merchantId)
                  .map((m) => m.merchantId)
                  .join(',');

                setParams({
                  excludeMerchant: merchantIds || '',
                  excludeUnassigned: willBeExcluded ? '1' : '',
                }); // Default: reset page to 1 when filter changes
              }}
              onCancel={handleCancelSelection}
              onClearSelection={() => {
                handleClearSelection();
                // getStationList() is called automatically via useEffect
                // when excludeMerchant state changes
              }}
              // Unassigned props
              excludeUnassigned={excludeUnassigned}
              pendingExcludeUnassigned={pendingExcludeUnassigned}
              onUnassignedToggle={handleUnassignedToggle}
              isLoadingMerchants={isLoadingMerchants}
              merchantGroups={merchantGroups}
              selectedGroup={selectedGroup}
              onGroupSelection={(group) =>
                handleGroupSelection(
                  group,
                  pendingExcludeMerchant,
                  setPendingExcludeMerchant,
                )
              }
              onCreateGroup={() => openCreateGroupModal('station')}
              onEditGroup={(group) => openEditGroupModal(group, 'station')}
              showGroups={true}
              showSearch={true}
              showSelectAll={true}
              showUnassigned={
                adminDetails?.role === sideBarRoutes?.Admin ||
                adminDetails?.role === sideBarRoutes?.Dealer
              }
              buttonWidth="200px"
            />
          </Box>

          {adminDetails?.role === sideBarRoutes?.Admin && (
            <Button
              backgroundColor={'green.500'}
              color="white"
              borderRadius="12px"
              fontWeight="500"
              _hover={{ backgroundColor: 'green.500', opacity: 0.8 }}
              isLoading={isStationSheetLoading}
              loadingText="Syncing..."
              onClick={() => dispatch(SyncStationSheet())}
            >
              Sync to Sheet
            </Button>
          )}
          <Button
            onClick={() => {
              resetAll();

              setExcludeMerchant([]);
              handleClearSelection();
              setExcludeUnassigned(false);
              setPendingExcludeUnassigned(false);
            }}
            bg="gray.200"
            color="gray.700"
            borderRadius="12px"
            fontWeight="500"
            _hover={{ bg: 'gray.300' }}
            px={6}
          >
            Reset
          </Button>
        </Flex>

        <Box ref={tableRef}>
          <CommonTable
            columns={[
              ...columns,
              ...(adminDetails?.role === sideBarRoutes?.Admin ||
              adminDetails?.role === sideBarRoutes?.Dealer ||
              adminDetails?.role === sideBarRoutes?.Seller
                ? action
                : []),
            ]}
            data={stationListData?.list}
            isLoading={isLoading}
            keyValue="stationId"
            isLink={true}
            parentLink="/admin/stations/details"
            getfilter={(e) => {
              // Use setParams to set both sortKey and sortBy together
              // This ensures both values are properly set in the URL
              setParams({
                sortKey: e.sortKey,
                sortBy: e.sortBy,
              });
            }}
            defaultFilter={{ sortKey: params.sortKey, sortBy: params.sortBy }}
            rowclick={['actions', 'stationId', 'merchantName']}
            excludes={[
              'pbStock',
              'faultySlots',
              'availablePBs',
              'activeOrderCount',
              'status',
              'posStatus',
              'lastLogTime',
              'offlineMinutes',
            ]}
            isStationWidth={true}
            dynamicWidth={{
              stationId: '120px',
              merchantName: '250px',
              location: '120px',
              activeOrderCount: '120px',
              pbStock: '120px',
              faultySlots: '120px',
              availablePBs: '130px',
              status: '130px',
              merchantHoursStatus: '280px',
              stationCondition: '250px',
              offlineMinutes: '240px',
              network: '100px',
              signal: '100px',
              posStatus: '120px',
              lastLogTime: '180px',
              actions: '70px',
            }}
            stickyColumns={['stationId']}
          />
        </Box>

        <CommonPagination
          totalCount={stationListData?.total}
          activePage={page}
          pageCount={Math.ceil(stationListData?.total / stationListData?.limit)}
          onPageChange={(e) => {
            setPage(e);
          }}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />

        <DeleteModal
          isOpen={isOpen}
          onClose={onClose}
          title="Are you sure you want to delete this station?"
          SolidBTNText="Delete"
          onDelete={() => {
            dispatch(
              DeleteStationData(stationDeleteRef.current, {}, () => {
                getStationList();
                onClose();
              }),
            );
          }}
          loader={isLoading}
        />

        {/* Group Management Modal for merchant groups in exclude filter */}
        <GroupManagementModal
          isOpen={isGroupModalOpen}
          onClose={closeGroupModal}
          groupModalData={groupModalData}
          setGroupModalData={setGroupModalData}
          merchantNameList={merchantNameList}
          onSubmit={handleGroupModalSubmit}
          onCancel={handleGroupModalCancel}
          onDelete={handleGroupModalDelete}
          isCreatingGroup={isCreatingGroup}
          isUpdatingGroup={isUpdatingGroup}
          isDeleting={isDeleting}
          onMerchantSelection={handleGroupMerchantSelection}
        />
      </Box>
    </>
  );
};

export default StationList;
